import {HttpClient} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {BehaviorSubject, catchError, firstValueFrom, map, switchMap, throwError} from "rxjs";
import {environment} from "src/environments/environment";
import {Utils} from "../../shared/utils/generics.utils";
import {WebappConfigurationDto} from "../dto/webapp-configuration-dto";
import {FeaturesConfigurationDto} from "../dto/features-configuration-dto";
import {ConfigurationDto} from "../dto/configuration-dto";
import {ReportConfigurationDto} from "../dto/report-configuration-dto";
import {TyreScannerConfigurationDto} from "../dto/tyre-scanner-configuration-dto";
import {DiagnosisConfigurationDto} from "../dto/DiagnosisConfigurationDto";
import {FleetcheckConfigurationDto} from "../dto/fleetcheck-configuration-dto";
import {UiUtils} from "../../shared/utils/ui.utils";
import {WebReportConfigurationDto} from "../dto/web-report-configuration-dto";
import * as Sentry from "@sentry/angular";
import {AppointmentConfigurationDto} from "../dto/appointment-configuration-dto";
import {CollectConfigurationDto} from "../dto/collect-configuration-dto";

@Injectable()
export class ConfigService {
  config?: Config = initialState;
  private configLoadedSubject = new BehaviorSubject<Config | null | undefined>(null);
  public configLoaded$ = this.configLoadedSubject.asObservable();

  readonly mercureUrl = 'https://ca-rool.com/.well-known/mercure';
  private readonly http = inject(HttpClient);
  private readonly legals = 'https://www.carool.tech/cgu'

  setTheme(config: Config) {
    let root = document.documentElement;
    config.webapp.isLightTheme ? root.setAttribute('class', 'light-theme') : root.setAttribute('class', 'colored-theme');
  }

  setFavicon(config: Config) {
    let favicon = document.createElement('link');
    favicon.rel = "icon";
    favicon.type = "image/x-icon";
    favicon.href = config.webapp.faviconImage;
    document.querySelector('head')?.appendChild(favicon);
  }

  setFont(config: Config) {
    let font = new FontFace('MyFont', 'url(' + config.report.font + ')', {
      style: 'normal',
      weight: '400',
      display: 'swap'
    });
    let fontBold = new FontFace('MyFont', 'url(' + config.report.fontBold + ')', {
      style: 'normal',
      weight: '700',
      display: 'swap'
    });
    (document.fonts as any).add(font);
    (document.fonts as any).add(fontBold);
    font.load().then();
    fontBold.load().then();
  }

  setGoogleKeys(config: Config) {
    console.log('[Config Service ] set google keys', config);
    if (config.appointment.workshopsProvider && config.google.placesApiKey) {
      let script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${config.google.placesApiKey}&libraries=places&callback=doNothing`
      document.querySelector('head')?.appendChild(script);
    }
  }

  setCustomCss(config: Config) {
    let link = document.createElement('link');
    link.href = config.webapp.customCss;
    link.rel = 'stylesheet';
    document.querySelector('head')?.appendChild(link);
  }

  loadConfig() {
    return firstValueFrom(this.http.get<Config>(`${environment.defaultConfigFile}`).pipe(
      switchMap((config: Config) => {
        this.config = config;
        return this.http.get<ConfigurationDto>(`${config.rool.configurationApi.url}/customers/${Utils.getCompanyFromDomain()}/configurations`)
      }),
      catchError((err) => {
        const company = Utils.getCompanyFromDomain();
        if (err.status === 404 && company.includes('-')) {
          Sentry.captureException(new Error('Unable to load company'), {extra: {company}})
          const fallbackCompany = company.split('-')[0];
          return this.http.get<ConfigurationDto>(`${this.config!.rool.configurationApi.url}/customers/${fallbackCompany}/configurations`)
        }
        return throwError(() => err);
      }),
      switchMap((configDto: ConfigurationDto) => {
        if (this.config) {
          this.config.features = configDto.feature;
          this.config.webapp = configDto.webapp;
          this.config.report = configDto.report;
          this.config.tyreScanner = configDto.tyreScanner;
          this.config.diagnosis = configDto.diagnosis;
          this.config.fleetcheck = configDto.fleetcheck;
          this.config.webReport = configDto.webReport;
          this.config.appointment = configDto.appointment;
          this.config.collect = configDto.webappCollect;

          this.setCustomCss(this.config);
          this.setTheme(this.config);
          this.setFavicon(this.config);
          this.setFont(this.config);
          this.setGoogleKeys(this.config);
        }

        return this.http.get<any>(this.config?.webapp.themeFile!, {responseType: 'json'});
      }), map((ui) => {
        let root = document.documentElement;
        Object.keys(ui).forEach(function (key) {
          root.style.setProperty(key, ui[key]);
        });
        this.configLoadedSubject.next(this.config);
      })
    ))
  }

  getConfig() {
    return this.config || initialState;
  }

  getFeatures() {
    return this.getConfig().features;
  }

  getWebapp() {
    return this.getConfig().webapp;
  }

  getTyreScanner() {
    return this.getConfig().tyreScanner;
  }

  getDiagnosis() {
    return this.getConfig().diagnosis;
  }

  getReport() {
    return this.getConfig().report;
  }

  getWebReport() {
    return this.getConfig().webReport;
  }

  getAppointment() {
    return this.getConfig().appointment;
  }

  getWorkshopsProviderName() {
    const provider = this.getAppointment().workshopsProvider;
    return provider ? Utils.capitalizeFirstLetter(provider.split('-')[0]) : undefined;
  }

  getCollect() {
    return this.getConfig().collect;
  }

  getEnv() {
    return this.getConfig().app.environment;
  }

  hasPhoneIdentification() {
    return this.getWebapp().showPhoneIdentification;
  }

  hasIdentification() {
    return this.getWebapp().showPhoneIdentification || this.getWebapp().showMailIdentification;
  }

  getLegalsUrl() {
    const browserLang = UiUtils.getBrowserLanguage();
    return browserLang.startsWith('fr') ? this.legals : this.legals.substring(0, this.legals.lastIndexOf('/')) + '/en' + this.legals.substring(this.legals.lastIndexOf('/'));
  }

  getPrivacyUrl() {
    const browserLang = UiUtils.getBrowserLanguage();
    const privacyUrl = this.getFeatures().privacyUrl;
    const isCaroolPrivacy = privacyUrl?.includes('carool.tech');

    return isCaroolPrivacy ?
      (browserLang.startsWith('fr') ? privacyUrl : privacyUrl!.substring(0, privacyUrl!.lastIndexOf('/')) + '/en' + privacyUrl!.substring(privacyUrl!.lastIndexOf('/')))
      : privacyUrl;
  }

}

export interface Config {
  app: {
    environment: string;
    disableDate?: string; // ISO format,
  };
  rool: {
    referenceApi: {
      url: string;
    };
    appointmentApi: {
      url: string;
    };
    configurationApi: {
      url: string;
    };
    externalApi: {
      url: string;
    };
  };
  webapp: WebappConfigurationDto,
  features: FeaturesConfigurationDto,
  report: ReportConfigurationDto,
  tyreScanner: TyreScannerConfigurationDto,
  diagnosis: DiagnosisConfigurationDto,
  fleetcheck: FleetcheckConfigurationDto,
  webReport: WebReportConfigurationDto,
  appointment: AppointmentConfigurationDto,
  collect: CollectConfigurationDto;
  google: {
    placesApiKey: string;
  }
}

export const initialState: Config = {
  app: {
    environment: ''
  },
  rool: {
    referenceApi: {
      url: ''
    },
    appointmentApi: {
      url: ''
    },
    configurationApi: {
      url: ''
    },
    externalApi: {
      url: ''
    },
  },
  webapp: {
    logoImage: '',
    logoHeight: 0,
    faviconImage: '',
    isProEnabled: false,
    isEmbedded: false,
    showMailIdentification: false,
    showPhoneIdentification: false,
    analysisExpirationDuration: 90,

    isLightTheme: false,
    themeFile: '',
    showLandingTitle: false,
    logoBackgroundColor: '',
    logoBorderColor: '',
    secondLogoImage: '',
    languagesFiles: [],
    adsBanner: undefined,
    customCss: '',
    enableSentry: false,
    betaFeaturesEnabled: false,
    allowEmbeddedVehicleChange: false,
    geolocationRestriction: [],
    contactFormStage: 'END'
  },
  features: {
    winterReglementationDetails: false,
    winterReglementationUrl: '',
    ctuCurrentVersion: 0,
    notSupportedImage: '',
    referenceMask: '',
    wearMask: '',
    displayCompanyName: '',
    enableEndUserOptInConsentCollect: false,
    privacyUrl: '',
    licenseAutorizedCountries: [],
    registrationCardTutorialIdeal: '/assets/img/tutorial/registration_ideal.png',
    registrationCardTutorialCropped: '/assets/img/tutorial/registration_cropped.png',
    registrationCardTutorialBlurred: '/assets/img/tutorial/registration_blurred.webp'
  },
  report: {
    font: '',
    fontBold: '',
    changeTyresLink: ''
  },
  tyreScanner: {
    enable: false,
    vehicleIdentificationScenario: [],
    hookActivated: true,
    enableGetVehicleInfo: false,
    referenceTyresScenario: [],
    wearTyresScenario: []
  },
  diagnosis: {
    enable: true,
    vehicleIdentificationScenario: [],
    mileageMandatory: false,
    enableReminder: true,
    enableSpareTyreAnalysis: false,
    enableNewDiagnosisWithOtherLicense: true,
    phoneAuthorizedCountries: [],
    enableSharing: true,
    landingImage1: '',
    landingImage2: '',
    referenceTutorialImage: '',
    wearTutorialImage: '',
    finalStepUrl: ''
  },
  fleetcheck: {
    enableReportPage: false
  },
  webReport: {
    reportAccessStrategies: []
  },
  appointment: {
    enable: false,
    desktopProviderAdsImage: '',
    workshopsProvider: ''
  },
  collect: {
    enabled: false,
    referenceTyresScenario: [],
    wearTyresScenario: [],
    vehicleIdentificationScenario: [],
  },
  google: {
    placesApiKey: ''
  }
};
